import { GetDirectoryPicker, GetHandlePermission } from "@pdfking/capability"
import type { Result } from "@pdfking/errors"
import { Failure, Success } from "@pdfking/errors"
import type { FileName, URLString } from "@pdfking/structs"
import { SafePromise } from "@pdfking/tfunc"

import type { RemoteFileSystem } from "#public"
import type { SingleValueCacheType } from "#public"
import type { UfsError } from "#public"

import { ImplementHandleFileSystem } from "../../internal/ImplementHandleFileSystem"
import { SingleValueCache } from "../../public/utils/SingleValueCache"
import { StorageUrl } from "../../public/utils/StorageUrl"
import { IndexedDBStore } from "../indexeddb/IndexedDBStore"

/** The database a grant is remembered in, unless the caller keeps its own —
 * its own, apart from whatever else is stored, because what is in here is a
 * grant rather than a file. */
const UFS_DB = "pdfking-ufs-handles"

/** Which directory in that database: one name per folder a caller wants back,
 * so two grants can live side by side without two databases. */
const UFS_GRANT = "Main"

// Where the grant survives a reload, and the only place it can: a directory
// handle is kept by structured clone or not at all, which is IndexedDB. Both
// names travel together everywhere below — a handle filed under one pair and
// looked for under another is a folder the user picked and never sees again.
const Handle = (database: string, grant: string): SingleValueCacheType<FileSystemDirectoryHandle> =>
  SingleValueCache(IndexedDBStore<FileSystemDirectoryHandle>(database), grant)

// Browsers without the handle permission API only hand out handles that are
// already usable, so "granted" is the honest answer there. `requestPermission`
// only grants while a user gesture is live: with a fresh handle that is the
// click that opened the picker, with a cached one it must be a click too —
// on page load it just answers "prompt" and the caller gets PERMISSION_DENIED.
const Grant = async (root: FileSystemDirectoryHandle): Promise<PermissionState> => {
  const permission = GetHandlePermission(root)
  if (!permission) return "granted"
  const state = await permission.query({ mode: "readwrite" })
  return state === "granted" ? state : permission.request({ mode: "readwrite" })
}

// Both names, said the way a url says a store: the grant is what the folder
// was filed under and the database is where, which is `user@host` — a colon
// between them would not parse, a port being the only thing a url expects
// there. Encoded, because neither is ours to spell.
const Authority = (database: string, grant: string): string =>
  `${encodeURIComponent(grant)}@${encodeURIComponent(database)}`

// One granted directory, written down under the database that names it: what
// is filed is a grant, not just a handle, because permission is settled first.
//
// Entries have urls, `ufs://{grant}@{database}/{path}`: unlike every other
// store here the folder is the user's, so what it takes to find it again is
// not a name we chose but the pair the grant is remembered under — which is
// what the authority carries, and why it carries two things.
const Settle = async (
  database: string,
  grant: string,
  root: FileSystemDirectoryHandle
): Promise<Result<RemoteFileSystem, UfsError>> => {
  const state = await Grant(root)
  if (state !== "granted") return Failure("FS_PERMISSION_DENIED", { name: root.name, state })
  await Handle(database, grant).set(root)

  return Success({
    ...ImplementHandleFileSystem(async () => root),

    url: (name: FileName): URLString => StorageUrl("ufs:", Authority(database, grant), name),
  })
}

/**
 * The user's own file system: a directory they picked once, opened again by
 * the database it was filed under. Paths inside it are the keys, and it only
 * exists once permission is settled — so nothing downstream deals with grants.
 *
 * Nothing is picked here: this is the recovery path after a reload, and the
 * grant it re-settles needs a live user gesture, so a call on page load can
 * only answer PERMISSION_DENIED. {@link OpenUfsFileSystem} is the other half —
 * the click that picks the directory in the first place.
 */
export const UfsFileSystem = async (
  database: string = UFS_DB,
  grant: string = UFS_GRANT
): Promise<Result<RemoteFileSystem, UfsError>> => {
  const root = await Handle(database, grant).get()
  return root ? Settle(database, grant, root) : Failure("FS_NO_HANDLE")
}

/**
 * The same file system, picked rather than remembered: the user chooses a
 * directory and it is filed under `database`, so every later open is
 * {@link UfsFileSystem}.
 *
 * Must be called from a user gesture: the picker needs one, and so does the
 * permission prompt that settling may raise straight after — without it the
 * picker rejects, which is a failure like any other here.
 */
export const OpenUfsFileSystem = async (
  database: string = UFS_DB,
  grant: string = UFS_GRANT
): Promise<Result<RemoteFileSystem, UfsError>> => {
  const picker = GetDirectoryPicker()
  if (!picker) {
    return Failure("FS_UNSUPPORTED")
  }

  // Called before any await so the click's transient activation still holds.
  // Only the picker's own rejection is read here: what settling does after
  // already answers with a result.
  const { error, value } = await SafePromise(picker({ mode: "readwrite" }))

  return error ? Failure("FS_PICKER_FAILED", { error }) : Settle(database, grant, value)
}

/** Drops the remembered directory: the next open has nothing to recover, so it
 * is the picker again. The grant is the browser's to revoke, not ours — what
 * goes is our claim to it. */
export const ForgetUfsFileSystem = (
  database: string = UFS_DB,
  grant: string = UFS_GRANT
): Promise<void> => Handle(database, grant).delete()
